import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { useSurveillanceRoutes } from './useSurveillanceRoutes';
import {
  getCachedRoadMesh,
  getCachedRoadMeshEntries,
} from './usePrecacheRouteMesh';
import {
  pkFromMesh,
  pkFromMeshEntries,
  PkFromMeshEntry,
  PkMeshVertex,
} from '../utils/pkFromMesh';
import type { RoadPkRange } from '../utils/pkValidation';
import {
  MIN_CONSECUTIVE_POINTS,
  pointsWithin,
  resolveClosingStretches,
} from '../utils/roadStretches';
import { showSuccessToast } from '../utils/toasts';
import {
  carryStateAcrossRoads,
  createDetectorState,
  isPkContinuous,
  rejectPending,
  stepDetector,
  DEFAULT_THRESHOLDS,
  DetectorState,
  Pivot,
  SegmentDirection,
} from '../utils/directionChangeDetector';
import type { LiveTrackPoint } from './useSurveillanceRouteGpsTracking';

export type DirectionBlindReason = 'no-mesh' | 'off-road';

export type PendingDirectionChange = {
  pivot: Pivot;
  direction: SegmentDirection;
  /** Dónde volvió a la vía si el giro fue por fuera (ver `DetectorState.resumedAt`). */
  resumedAt?: Pivot | null;
};

type Options = {
  routeId?: string;
  /** Carretera con la que se abrió el tramo. El detector empieza vigilándola y la sigue si la patrulla cambia. */
  roadId?: string;
  /** Todas las carreteras del recorrido: el detector sigue a la patrulla por cualquiera de ellas. */
  routeRoadIds?: string[];
  /** Rangos declarados de esas carreteras, para anotar los trozos del tramo que cierra. */
  routeRoadRanges?: Record<string, RoadPkRange>;
  /**
   * `direction` hace falta para proyectar sobre LA CALZADA del tramo: la malla
   * canónica es la de dibujar, y usarla aquí mete cola de error en las dos
   * señales del detector (medido: p95 de 84 m en la C-17). El resto describe
   * el tramo que se cierra al registrar un giro.
   */
  activeSegment: {
    id: string;
    direction?: string | null;
    roadId?: string | null;
    startDate?: string | null;
    pkInitKm?: number | null;
    pkInitMeter?: number | null;
  } | null;
  liveTrack: LiveTrackPoint[];
  enabled: boolean;
  /**
   * Vías de APOYO (2026-09-23): las de la concesión que el recorrido no
   * declara. NO entran en el detector —su universo siguen siendo las del
   * recorrido, afinado con trazas de campo—; solo sirven para decir EN CUÁL
   * está la patrulla cuando el aviso es «fuera del trazado», y ofrecer
   * añadirla. Una vez añadida es del recorrido y el detector la sigue.
   */
  supportRoadIds?: string[];
};

/**
 * Rastro de la vía de APOYO, SOLO en desarrollo (2026-09-29).
 *
 * Probando con el usuario, la vía no se añadió al llegar al ramal y no había
 * nada que mirar: ni error, ni petición. Mismo criterio que `[pk-prefill]`:
 * sin esto, diagnosticarlo es adivinar. `import.meta.env.DEV` lo deja fuera
 * del bundle de producción.
 */
const traceSupportRoad = (step: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.log(`[support-road] ${step}`, data ?? '');
  }
};

/**
 * Adaptador entre la traza GPS viva y el núcleo del detector de cambio de
 * sentido (`utils/directionChangeDetector`).
 *
 * No captura GPS propio: se engancha a los puntos que
 * `useSurveillanceRouteGpsTracking` ya produce, así que no cuesta ni una
 * lectura más de batería. Y no decide nada por su cuenta: toda la lógica vive
 * en `stepDetector`, que es puro y por eso testeable con trazas sintéticas y
 * reproducible con trazas reales.
 *
 * 🔴 Al registrar el cambio, el corte se estampa en el **PIVOTE** —el punto de
 * giro real— y no en el instante de la confirmación, que llega cientos de
 * metros después. Los campos que hacen falta para eso (`endDate`,
 * `registerInitDate` y las coordenadas de inicio/fin) ya los acepta el backend:
 * se añadieron para que una operación encolada sin cobertura no se sellara con
 * la hora de la reconexión. De ahí que esta función no necesite backend nuevo.
 *
 * El tramo que se cierra va **sin carril** a propósito: el carril es del tramo
 * que se cierra de verdad (modal Finalizar), no de un pivote de sentido.
 */
export function useDirectionChangeDetection({
  routeId,
  roadId,
  routeRoadIds,
  routeRoadRanges,
  activeSegment,
  liveTrack,
  enabled,
  supportRoadIds,
}: Options): {
  pending: PendingDirectionChange | null;
  blindReason: DirectionBlindReason | null;
  /** Con `blindReason === 'off-road'`: la vía de apoyo que pisa, si es una. */
  offRouteRoadId: string | null;
  /**
   * La vía de apoyo que hay que AÑADIR al recorrido: la patrulla lleva
   * `MIN_CONSECUTIVE_POINTS` lecturas buenas seguidas en ella. `null` mientras
   * no. Quien monta el detector la añade sola y avisa (2026-09-29).
   */
  supportRoadToAdopt: string | null;
  isExecuting: boolean;
  confirm: () => Promise<void>;
  dismiss: () => void;
} {
  const { t } = useTranslation(['modals', 'toasts']);
  const { createSegment, finishSegment } = useSurveillanceRoutes();
  const revalidator = useRevalidator();

  const stateRef = useRef<DetectorState>(createDetectorState());
  /** Timestamp (ms) del último punto ya procesado. */
  const cursorRef = useRef<number>(0);
  const executingRef = useRef(false);
  /**
   * Vía que se vigila AHORA: la del tramo al abrirse, y la que pise la
   * patrulla después. Antes era siempre la de apertura y, en cuanto la
   * patrulla se pasaba a otra vía del recorrido, el detector proyectaba sobre
   * una malla que ya no pisaba: ciego hasta cerrar el tramo (C-25, 2026-09-02,
   * cuatro giros de una tarde sin registrar).
   */
  const currentRoadRef = useRef<string | null>(null);
  /** Calzada de esa vía sobre la que se proyecta (al cruzar, la más cercana). */
  const currentMeshRef = useRef<PkMeshVertex[] | null>(null);
  /** Vía candidata a la que se está cambiando y cuántos puntos seguidos la avalan. */
  const candidateRef = useRef<{ roadId: string; count: number } | null>(null);
  /**
   * Última proyección aceptada SOBRE la vía vigilada: contra ella se mide la
   * continuidad de PK al cruzar a otra vía (`isPkContinuous`).
   */
  const lastHitOnRoadRef = useRef<{ pkKm: number; pkMeter: number } | null>(null);
  /**
   * Vía de APOYO que pisa la patrulla y cuántas lecturas buenas seguidas la
   * avalan. Vive entre lotes: el GPS no entrega los puntos de tres en tres.
   */
  const supportStreakRef = useRef<{ roadId: string; count: number } | null>(null);
  // Latest-refs del tramo y de la vía de apertura: `execute` los lee al
  // registrar el giro sin meterlos en las dependencias de los efectos.
  const activeSegmentRef = useRef(activeSegment);
  activeSegmentRef.current = activeSegment;
  const roadIdRef = useRef(roadId);
  roadIdRef.current = roadId;

  const [pending, setPending] = useState<PendingDirectionChange | null>(null);
  const [blindReason, setBlindReason] = useState<DirectionBlindReason | null>(
    null
  );
  const [offRouteRoadId, setOffRouteRoadId] = useState<string | null>(null);
  const [supportRoadToAdopt, setSupportRoadToAdopt] = useState<string | null>(
    null
  );
  const [isExecuting, setIsExecuting] = useState(false);

  // La traza más reciente, para poder consultarla desde el efecto de reinicio
  // sin meterla en sus dependencias (si no, cada punto reiniciaría el estado).
  const liveTrackRef = useRef(liveTrack);
  liveTrackRef.current = liveTrack;

  // Un tramo NUEVO reinicia el detector: la evidencia acumulada en el tramo
  // anterior no dice nada del actual.
  const activeSegmentId = activeSegment?.id ?? null;
  const activeDirection = activeSegment?.direction ?? null;
  /** Último id de tramo con el que se reinició. Distingue «nuevo» de «ha vuelto». */
  const lastSegmentIdRef = useRef<string | null>(null);
  useEffect(() => {
    // 🔴 Solo un tramo nuevo y DISTINTO reinicia. `activeSegment` sale de una
    // query sondeada cada 30 s: un sondeo fallido deja `data` en `undefined`,
    // el id pasa a `null` un ciclo y vuelve a ser el mismo. Si eso reiniciara,
    // el detector perdería el extremo y el pivote en mitad de un giro — y no se
    // notaría: nacería de nuevo en la vuelta, con el sentido al revés, y
    // estamparía el corte en el siguiente sitio donde diera la vuelta. Pasó en
    // la C-25 el 2026-09-02 a las 11:26: el núcleo, reproducido sobre la traza,
    // confirma en 193+914; la app registró 187+162.
    //
    // Una ausencia transitoria conserva estado, cursor y aviso pendiente. El
    // reinicio ante tramo nuevo se queda tal cual: es lo que evita la cascada
    // de los 128 tramos (ver más abajo).
    if (activeSegmentId === null) return;
    if (activeSegmentId === lastSegmentIdRef.current) return;
    lastSegmentIdRef.current = activeSegmentId;
    stateRef.current = createDetectorState();
    // Un tramo nuevo se vigila desde su carretera de apertura.
    currentRoadRef.current = null;
    currentMeshRef.current = null;
    candidateRef.current = null;
    lastHitOnRoadRef.current = null;
    supportStreakRef.current = null;
    setSupportRoadToAdopt(null);
    // 🔴 El cursor NO vuelve a cero: es la marca de agua de lo ya consumido, y
    // rebobinarla reprocesa la traza entera. Registrar un cambio crea un tramo,
    // o sea que cambia `activeSegmentId` y dispara este mismo efecto: con el
    // cursor a cero los puntos que acababan de demostrar el giro se volvían a
    // procesar, confirmaban otra vez, creaban otro tramo… en bucle. Cazado el
    // 2026-08-15 contra beta: 128 tramos de longitud cero compartiendo dos
    // marcas de tiempo, que son la MISMA detección repetida.
    //
    // Se avanza hasta el último punto conocido en vez de dejarlo donde estaba
    // para no replicar el problema en el otro extremo: al entrar en la pantalla
    // la traza viene rehidratada, y arrancar desde el principio replayaría un
    // giro de hace horas registrándolo como si acabara de pasar.
    cursorRef.current = liveTrackRef.current.reduce(
      (max, p) => Math.max(max, new Date(p.timestamp).getTime()),
      cursorRef.current
    );
    setPending(null);
  }, [activeSegmentId]);

  /**
   * Por qué carreteras pasó el tramo que se cierra, sacado del rastro hasta el
   * pivote — la misma regla que el cierre manual (`PauseSurveillanceRouteModal`).
   *
   * Sin esto, un pivote en la franja que la troncal y su ramal comparten
   * (234+000 → 236+000 en la C-25) cabe en la vía de apertura y el servidor
   * no deduce nada: el tramo quedaría anotado como C-25 entera.
   */
  const closingStretches = useCallback(
    (pivot: Pivot) => {
      const seg = activeSegmentRef.current;
      if (
        !routeRoadIds ||
        routeRoadIds.length < 2 ||
        !routeRoadRanges ||
        !seg?.roadId ||
        seg.pkInitKm == null ||
        seg.pkInitMeter == null
      ) {
        return null;
      }
      const points = pointsWithin(
        liveTrackRef.current.map((p) => ({
          latitude: p.lat,
          longitude: p.lng,
          timestamp: p.timestamp,
        })),
        seg.startDate,
        new Date(pivot.timestamp)
      );
      return resolveClosingStretches({
        pk: { km: pivot.pkKm, meter: pivot.pkMeter },
        pkInit: { km: seg.pkInitKm, meter: seg.pkInitMeter },
        segmentRoadId: seg.roadId,
        roadIds: routeRoadIds,
        rangesById: routeRoadRanges,
        points,
        meshEntries: getCachedRoadMeshEntries(routeRoadIds),
      });
    },
    [routeRoadIds, routeRoadRanges]
  );

  const execute = useCallback(
    async (
      pivot: Pivot,
      direction: SegmentDirection,
      resumedAt: Pivot | null = null
    ) => {
      if (!routeId || !activeSegmentId || executingRef.current) return;
      // Guard de ejecución única: dos cierres simultáneos chocarían con el
      // «solo un timer activo» del backend, y el segundo se perdería.
      executingRef.current = true;
      setIsExecuting(true);
      const at = new Date(pivot.timestamp).toISOString();
      // Si el giro fue por fuera de la vía, el tramo nuevo nace donde la
      // patrulla se reincorporó, no donde salió: el tiempo fuera no es de
      // ningún tramo. Sin hueco los dos puntos coinciden.
      const start = resumedAt ?? pivot;
      const startAt = new Date(start.timestamp).toISOString();
      // La vía vigilada en el pivote: la de apertura si la patrulla no cambió,
      // la que pisaba si se pasó a otra del recorrido.
      const road = currentRoadRef.current ?? roadIdRef.current;
      try {
        await finishSegment(
          {
            id: activeSegmentId,
            endDate: at,
            pkEndKm: pivot.pkKm,
            pkEndMeter: pivot.pkMeter,
            endLatitude: pivot.lat,
            endLongitude: pivot.lng,
            pauseCause: 'basic',
            pauseCauseDetail: t('modals:changeDirection.description'),
            roadStretches: closingStretches(pivot) ?? undefined,
          },
          { silentSuccess: true }
        );
        await createSegment(
          {
            surveillanceRouteId: routeId,
            roadId: road,
            direction,
            pkInitKm: start.pkKm,
            pkInitMeter: start.pkMeter,
            initLatitude: start.lat,
            initLongitude: start.lng,
            registerInitDate: startAt,
          },
          { silentSuccess: true }
        );
        showSuccessToast(t('toasts:success.updated'));
        revalidator.revalidate();
      } catch (err) {
        // El toast de negocio lo emite `useSurveillanceRoutes`
        // (backend-message-first) — aquí no se duplica.
        Sentry.captureException(err, {
          tags: {
            feature: 'surveillance-route',
            hook: 'useDirectionChangeDetection',
          },
        });
      } finally {
        setPending(null);
        executingRef.current = false;
        setIsExecuting(false);
      }
    },
    [
      routeId,
      activeSegmentId,
      finishSegment,
      createSegment,
      revalidator,
      t,
      closingStretches,
    ]
  );

  useEffect(() => {
    // El permiso denegado NO se cuenta aquí: cuando pasa, la captura abre el
    // modal bloqueante de «Es necesario reactivar el GPS», que tapa la pantalla
    // y ya explica qué hacer. Decirlo además debajo de la ficha era repetirlo
    // en un sitio que el operario ni ve (feedback del usuario, 2026-08-15).
    if (!enabled || !activeSegmentId || !roadId) return;
    // 🔴 La calzada POR LA QUE VA el tramo, no la canónica (2026-08-26).
    //
    // `getCachedRoadMesh` sirve la que más vértices tenga, y esa elección se
    // hizo para PINTAR el mapa. Aquí no se pinta: se proyecta la posición y se
    // mide el rumbo del eje, que son las dos señales del detector. Medido,
    // proyectar sobre la calzada equivocada tiene mediana 0-3 m pero cola larga
    // — p95 de 84 m y máximo de 309 en la C-17.
    // Las mallas de TODAS las vías del recorrido: el detector sigue a la
    // patrulla por la que pise, no por la que abrió el tramo.
    const allEntries = getCachedRoadMeshEntries(
      routeRoadIds?.length ? routeRoadIds : [roadId]
    );
    if (!currentRoadRef.current) currentRoadRef.current = roadId;
    const meshOfCurrentRoad = (): PkMeshVertex[] => {
      if (currentMeshRef.current) return currentMeshRef.current;
      const road = currentRoadRef.current ?? roadId;
      const entradas = allEntries.filter((e) => e.roadId === road);
      const suya = entradas.find((e) => e.direction === activeDirection);
      return (suya ?? entradas[0])?.vertices ?? getCachedRoadMesh(road);
    };
    const fresh = liveTrack.filter(
      (p) => new Date(p.timestamp).getTime() > cursorRef.current
    );
    if (fresh.length === 0) return;
    // Las mallas de las vías de APOYO, una vez por lote como las del recorrido.
    const supportEntries = supportRoadIds?.length
      ? getCachedRoadMeshEntries(supportRoadIds)
      : [];
    traceSupportRoad('lote', {
      points: fresh.length,
      candidates: supportRoadIds ?? [],
      supportMeshes: supportEntries.length,
    });

    let blind: 'no-mesh' | 'off-road' | null = null;
    let anyOnRoad = false;
    for (const p of fresh) {
      const timestamp = new Date(p.timestamp).getTime();
      cursorRef.current = timestamp;

      // ¿Sobre qué vía del recorrido cae? La misma regla que el resto de la
      // app: la más cercana, salvo empate técnico a favor de la vigilada (los
      // 50 m del asfalto que la troncal y su ramal comparten). Y hacen falta
      // `MIN_CONSECUTIVE_POINTS` seguidos en otra vía para cambiar, como al
      // anotar los trozos: un rebote bajo un puente no es un cruce.
      const hit = pkFromMeshEntries(
        p.lat,
        p.lng,
        allEntries,
        currentRoadRef.current
      );
      if (hit) anyOnRoad = true;

      // ¿Sigue por una vía de APOYO? (2026-09-29). Cuenta lecturas buenas
      // seguidas en la misma: una en la vía del recorrido corta la racha, y una
      // de mala precisión no dice nada —el núcleo tampoco la usa—. Con
      // `MIN_CONSECUTIVE_POINTS` se añade al recorrido: se ha cambiado de
      // carretera, no es un rebote.
      if ((p.accuracy ?? 0) <= DEFAULT_THRESHOLDS.maxAccuracyMeters) {
        const supportHit =
          !hit && supportEntries.length > 0
            ? pkFromMeshEntries(p.lat, p.lng, supportEntries, null)
            : null;
        const streak = supportStreakRef.current;
        supportStreakRef.current = supportHit
          ? {
              roadId: supportHit.roadId,
              count: streak?.roadId === supportHit.roadId ? streak.count + 1 : 1,
            }
          : null;
      }
      traceSupportRoad('punto', {
        lat: p.lat,
        lng: p.lng,
        accuracy: p.accuracy,
        routeRoad: hit?.roadId ?? null,
        streak: supportStreakRef.current,
      });

      if (hit && hit.roadId === currentRoadRef.current) {
        lastHitOnRoadRef.current = { pkKm: hit.pkKm, pkMeter: hit.pkMeter };
      }
      if (hit && hit.roadId !== currentRoadRef.current) {
        const c = candidateRef.current;
        candidateRef.current =
          c && c.roadId === hit.roadId
            ? { roadId: hit.roadId, count: c.count + 1 }
            : { roadId: hit.roadId, count: 1 };
        if (candidateRef.current.count >= MIN_CONSECUTIVE_POINTS) {
          // Cambio de vía. La memoria del detector cruza con la patrulla si las
          // dos vías son el mismo eje de PK (la C-25z hereda el kilometraje de
          // la C-25: continuidad medida en el cruce); si no, nace de nuevo —los
          // metros de asfalto de dos carreteras no son comparables, ni sus
          // etiquetas de sentido (medido: la C-35 `top` y la C-65 `top` no se
          // tocan)— y, en la vía del propio tramo, arranca con el sentido
          // DECLARADO como referencia. Ver `carryStateAcrossRoads`.
          //
          // Antes siempre nacía de nuevo: C-25 → C-25z, 2026-09-09, borró el
          // pivote 5 s antes de una vuelta dentro del ramal y el primer
          // movimiento aceptado (ya al revés) se convirtió en «lo normal».
          // Misma cadena de respaldo que `meshOfCurrentRoad()`: la calzada más
          // cercana y, si no hay, la de la vía por su sentido o la canónica.
          currentRoadRef.current = hit.roadId;
          currentMeshRef.current = nearestCarriageway(
            allEntries.filter((e) => e.roadId === hit.roadId),
            p
          );
          const newMesh = meshOfCurrentRoad();
          const continuous =
            lastHitOnRoadRef.current != null &&
            isPkContinuous(
              lastHitOnRoadRef.current,
              hit,
              DEFAULT_THRESHOLDS.roadHandoffMaxPkGapMeters
            );
          const declared = activeSegmentRef.current?.direction;
          const fallbackObserved =
            !continuous &&
            hit.roadId === activeSegmentRef.current?.roadId &&
            (declared === 'top' || declared === 'down')
              ? declared
              : null;
          candidateRef.current = null;
          lastHitOnRoadRef.current = { pkKm: hit.pkKm, pkMeter: hit.pkMeter };
          stateRef.current = carryStateAcrossRoads(
            stateRef.current,
            { continuous, mesh: newMesh, fallbackObserved },
            DEFAULT_THRESHOLDS
          );
          // El aviso pendiente sigue vivo si su pivote sobrevivió al cruce
          // (re-etiquetado en la vía nueva); si no, se retira como antes.
          const carriedPivot = stateRef.current.extremePivot;
          setPending((prev) =>
            prev && carriedPivot ? { ...prev, pivot: carriedPivot } : null
          );
        }
      } else {
        candidateRef.current = null;
      }

      const { state, decision } = stepDetector(
        stateRef.current,
        {
          lat: p.lat,
          lng: p.lng,
          timestamp,
          accuracy: p.accuracy,
          speed: p.speed,
          heading: p.heading,
        },
        {
          mesh: meshOfCurrentRoad(),
          // Las dos calzadas de la vía vigilada: tras la vuelta se circula
          // por la otra, y el tope de evidencia tiene que verla.
          carriageways: allEntries
            .filter((e) => e.roadId === currentRoadRef.current)
            .map((e) => e.vertices),
          thresholds: DEFAULT_THRESHOLDS,
        }
      );
      stateRef.current = state;
      if (decision.kind === 'blind') {
        blind = decision.reason;
      }
      if (decision.kind === 'suspect') {
        blind = null;
        setPending({
          pivot: decision.pivot,
          direction: decision.direction,
          resumedAt: state.resumedAt,
        });
      }
      if (decision.kind === 'confirm') {
        blind = null;
        void execute(
          decision.pivot,
          decision.direction,
          decision.resumedAt ?? null
        );
      }
    }
    // 🔴 «Fuera del trazado» solo si de verdad se salió de TODAS las vías del
    // recorrido. Mientras la patrulla se pasa a otra vía (los puntos que aún
    // no bastan para cambiar) el detector proyecta sobre la anterior y
    // devuelve `off-road`: cierto para el detector y FALSO para el operario,
    // y decírselo en rojo es una alarma por algo que no ha hecho mal.
    //
    // Se mira TODO el lote, no solo el último punto: un GPS suelta lecturas
    // sueltas fuera de trazado (un puente, un túnel, un rebote), y con el
    // último bastaría una para que el aviso rojo volviera a aparecer un
    // segundo y se fuera. Un aviso que parpadea es peor que no tenerlo:
    // el operario mira, no lee nada, y deja de fiarse del rojo.
    if (blind === 'off-road' && anyOnRoad) blind = null;
    setBlindReason(blind);

    // ¿Fuera del recorrido, pero en una vía de apoyo? (2026-09-23). El punto
    // más reciente del lote que caiga en una: es donde está AHORA. Sin
    // preferencia entre ellas, y sin tocar el estado del detector — esto solo
    // da nombre al aviso y la vía que ofrecer.
    let offRouteRoad: string | null = null;
    if (blind === 'off-road' && supportEntries.length > 0) {
      for (let i = fresh.length - 1; i >= 0 && !offRouteRoad; i -= 1) {
        const hit = pkFromMeshEntries(
          fresh[i].lat,
          fresh[i].lng,
          supportEntries,
          null
        );
        if (hit) offRouteRoad = hit.roadId;
      }
    }
    setOffRouteRoadId(offRouteRoad);
    const streak = supportStreakRef.current;
    const roadToAdopt =
      streak && streak.count >= MIN_CONSECUTIVE_POINTS ? streak.roadId : null;
    if (roadToAdopt) traceSupportRoad('PIDE AÑADIR', roadToAdopt);
    setSupportRoadToAdopt(roadToAdopt);
  }, [
    liveTrack,
    enabled,
    activeSegmentId,
    roadId,
    routeRoadIds,
    execute,
    supportRoadIds,
  ]);

  const dismiss = useCallback(() => {
    stateRef.current = rejectPending(stateRef.current);
    setPending(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!pending) return;
    await execute(pending.pivot, pending.direction, pending.resumedAt ?? null);
  }, [pending, execute]);

  return {
    pending,
    blindReason,
    offRouteRoadId,
    supportRoadToAdopt,
    isExecuting,
    confirm,
    dismiss,
  };
}

export default useDirectionChangeDetection;

/**
 * La calzada de `entries` más cercana a un punto. Al cruzar de vía no vale la
 * etiqueta de sentido del tramo: las etiquetas no son comparables entre
 * carreteras, y la calzada correcta es la que la patrulla pisa.
 */
function nearestCarriageway(
  entries: PkFromMeshEntry[],
  p: { lat: number; lng: number }
): PkMeshVertex[] | null {
  let best: { distance: number; vertices: PkMeshVertex[] } | null = null;
  for (const e of entries) {
    const hit = pkFromMesh(p.lat, p.lng, e.vertices);
    if (hit && (!best || hit.distance < best.distance)) {
      best = { distance: hit.distance, vertices: e.vertices };
    }
  }
  return best?.vertices ?? entries[0]?.vertices ?? null;
}
